import { checkSkillUse, useSkill as resolveSkill } from '../combat.ts';
import { type Dir, isDir } from '../grid.ts';
import { assertLeaderMayAct, faceUnit, runRound } from '../round.ts';
import { InvalidCommand } from '../state.ts';
import { type CommandHandler, chain } from './types.ts';

/**
 * The leader faces `dir` (if given) and uses one of its skills, then the
 * round runs. `target` is a unit id, required for targeting shape `single`
 * and forbidden otherwise. An aetherial skill without `noCharge` (and without
 * a `skip_charge` passive) starts a charge instead; the leader then sends
 * `wait` to fire it. Throws unless the skill could be used right now.
 */
export type UseSkillCommand = {
  readonly type: 'use_skill';
  readonly skill: string;
  readonly dir?: Dir;
  readonly target?: string;
};

export const useSkill: CommandHandler<UseSkillCommand> = (ctx, state, command) => {
  const leader = assertLeaderMayAct(state, 'other');
  // Logs arrive as JSON, so the payload is checked at run time too.
  if (typeof command.skill !== 'string') throw new InvalidCommand('skill_not_an_id', 'skill must be a skill id');
  if (command.dir !== undefined && !isDir(command.dir)) throw new InvalidCommand('bad_direction', 'bad direction');
  if (command.target !== undefined && typeof command.target !== 'string') throw new InvalidCommand('target_not_unit_id', 'target must be a unit id');
  const facing = command.dir ?? leader.facing;
  const target = command.target ?? null;
  checkSkillUse(ctx, state, leader.id, command.skill, facing, target, false);
  return chain(
    state,
    (s) => faceUnit(s, leader.id, facing),
    (s) => runRound(ctx, s, (x) => resolveSkill(ctx, x, leader.id, command.skill, facing, target), 'busy'),
  );
};
